import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import panzoom, { PanZoom, Transform } from "panzoom";
import { getMapImageWithTheme } from "modules/theater/theater.helper";
import { isTypeOfIMapTemplate } from "modules/event/template";
import classNames from "classnames";
import { ISponsor } from "modules/event/types";
import { useSubscribeToLockTables } from "modules/lockTables/hooks";
import { SVG_HEIGHT, SVG_WIDTH, SVG_ZOOM } from "modules/event";
import { selectIsMobile } from "modules/deviceInfo";
import { EventContentWrapper } from "modules/eventContent";
import logger from "logging/logger";
import { LayoutComponent } from "./templateParts/LayoutComponent";
import "./svg.scss";
import BackgroundSVG from "./templateParts/Background.svg";
import { selectThemeAndTemplate } from "../theater/selectors";
import { selectCurrentSpaceId } from "../space/redux/selectors";
import { usePrevious } from "../../helpers/reactHooksUtils";
import { selectCurrentEvent } from "../event/selectors";
import { syncSponsorsForEvent } from "./templateParts/sponsors.firebase";
import { useMapView } from "./templateParts/MapViewContext";
import { useStyles } from "./styles";

interface Props {
  isLoading: boolean;
}

const SVGMapView = ({ isLoading }: Props) => {
  const { setPanZoom } = useMapView();
  const selectedCurrentSpaceId = useSelector(selectCurrentSpaceId);
  const event = useSelector(selectCurrentEvent);
  const { theme, template } = useSelector(selectThemeAndTemplate);
  const [resetCenter, setResetCenter] = useState(true);
  const [instance, setInstance] = useState<PanZoom | null>(null);
  const panzoomedRef = useRef<HTMLDivElement | null>(null);
  const isMobileView = useSelector(selectIsMobile);
  const [sponsors, setSponsors] = useState<ISponsor[]>([]);
  const [isSponsorsLoaded, setIsSponsorsLoading] = useState(false);
  const willChangeResetTimeout = useRef<number>();
  const zoomAtWillChangeReset = useRef<number>(0.5);
  const classes = useStyles();

  const props = {
    spaceConfig: template,
    event,
    theme,
    template,
    currentSpaceId: selectedCurrentSpaceId,
    isLoading,
  };

  useSubscribeToLockTables({
    eventId: event?.id,
    floorId: selectedCurrentSpaceId,
  });

  const prevProps = usePrevious(props);

  const resetMapToCenter = () => {
    if (!isMobileView) {
      setResetCenter(false);

      return;
    }
    // TODO: fine tune to fix the avatar into center - not too much to top or bottom
    const avatar = document.getElementById("map-av-me");

    if (avatar && avatar.parentNode && instance) {
      const { scale } = instance.getTransform();

      const dex = (avatar.parentNode as HTMLElement).offsetLeft;
      const dey = (avatar.parentNode as HTMLElement).offsetTop;

      const dx = dex - (dex - scale * dex);

      // 120px for map banner and floor
      const dy = dey - (dey - scale * dey) - 120;

      instance.moveTo(-dx, -dy);
      setResetCenter(false);
    } else {
      setTimeout(() => {
        resetMapToCenter();
      }, 1000);
    }
  };

  const reposition = (pzInstance: PanZoom) => {
    if (isTypeOfIMapTemplate(template)) {
      const viewbox = template.conferenceArea || {};
      // 60 - map banner, 5 - top of banner, 15 extra tolerance
      const topOffset = 60 + 5 - 15;

      const defaultZoom = Math.min(
        window.innerWidth / viewbox.width,
        (window.innerHeight - topOffset) / viewbox.height,
      );

      if (viewbox.width === 0 || viewbox.height === 0) {
        logger.warn(
          "[svgMapView][reposition] viewbox width/height is 0, this might not be intentional:",
          viewbox,
        );
      }

      if (window.innerWidth === 0) {
        logger.warn(
          "[svgMapView][reposition] window innerWidth is 0, this might not be intentional:",
          viewbox,
        );
      }

      const left = -(
        viewbox.x * defaultZoom -
        (window.innerWidth - viewbox.width * defaultZoom) / 2
      );
      const top = topOffset - viewbox.y * defaultZoom + 30;
      // -(viewbox.y * defaultZoom - (window.innerHeight - 65 - 78 - viewbox.height * defaultZoom) / 2);

      pzInstance.zoomAbs(
        0, // initial x position (doesn't work for some reason)
        0, // initial y position (doesn't work for some reason)
        defaultZoom || SVG_ZOOM.min, // initial zoom - default to 0.6
      );

      if (!Number.isNaN(left) && !Number.isNaN(top)) {
        pzInstance.moveTo(left, top); // initial position
      }

      setResetCenter(true);
    }
  };

  const initializePanzoom = () => {
    if (instance) {
      reposition(instance);

      return;
    }

    if (!isTypeOfIMapTemplate(template)) {
      return;
    }
    // Get from space config or use default values
    const zoom = template.zoom ?? SVG_ZOOM;
    const panzoomInstance = panzoom(panzoomedRef.current as HTMLDivElement, {
      maxZoom: zoom.max,
      minZoom: zoom.min,
      bounds: true, // setting to true, so the map will not go out of view port https://remoapp.atlassian.net/browse/RC-1645
      boundsPadding: 0.1,
      zoomDoubleClickSpeed: 1,
      onDoubleClick: (e) => {
        e.preventDefault();

        // Add `e` in the params to use - `e` is current double click event.
        return false; // tells the library to not preventDefault, and not stop propagation
      },
      onTouch: () =>
        // Add `e` in the params to use - `e` is current touch event.
        false, // tells the library to not preventDefault.
    });

    /**
     * Generic handler for events fired from PanZoom library
     * @param eventName name of the event triggered (PanZoom library)
     * @param callback fn to run
     */
    const panZoomEventHandler = (
      eventName: string,
      callback: (transform: Transform) => void,
    ) => {
      if (panzoomedRef.current) {
        const transformValue = panzoomInstance.getTransform();

        callback(transformValue);
      }
    };

    panzoomInstance.on("panstart", () => {
      panZoomEventHandler("panstart", () => {
        if (panzoomedRef.current) {
          panzoomedRef.current.style.cursor = "move";
        }
      });
    });

    panzoomInstance.on("panend", () => {
      panZoomEventHandler("panend", () => {
        if (panzoomedRef.current) {
          panzoomedRef.current.style.cursor = "default";
        }
      });
    });

    panzoomInstance.on("zoom", () => {
      panZoomEventHandler("zoom", ({ scale }: Transform) => {
        const { max: maxZoom, min: minZoom } = template.zoom || SVG_ZOOM;

        if (
          Math.abs(zoomAtWillChangeReset.current - scale) < 0.5 &&
          scale !== maxZoom &&
          scale !== minZoom
        ) {
          return;
        }

        if (willChangeResetTimeout.current) {
          window.clearTimeout(willChangeResetTimeout.current);
          willChangeResetTimeout.current = undefined;
        }

        if (!willChangeResetTimeout.current) {
          willChangeResetTimeout.current = window.setTimeout(() => {
            if (!panzoomedRef.current) return;
            panzoomedRef.current.style.willChange = "unset";
            setTimeout(() => {
              if (!panzoomedRef.current) return;
              panzoomedRef.current.style.willChange = "transform";
              zoomAtWillChangeReset.current = scale;
            }, 40);
          }, 100);
        }
      });
    });

    setInstance(panzoomInstance);
    setPanZoom(panzoomInstance);
    reposition(panzoomInstance);
  };

  const loadSponsors = async () => {
    if (!isTypeOfIMapTemplate(template)) {
      return;
    }

    if (template?.sponsors && event?.id) {
      syncSponsorsForEvent(event.id, (newSponsors: ISponsor[] | null) => {
        if (newSponsors) {
          setSponsors(newSponsors);
        }
        setIsSponsorsLoading(true);
      });
    }
  };

  useEffect(() => {
    loadSponsors();
    initializePanzoom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrentSpaceId]);

  useEffect(() => {
    if (!isTypeOfIMapTemplate(template)) {
      return;
    }

    const { currentSpaceId } = props;

    const isSpaceChanged =
      currentSpaceId &&
      currentSpaceId !== (prevProps && prevProps.currentSpaceId);

    const currentMapImage = theme ? getMapImageWithTheme(template, theme) : "";

    const prevMapImage =
      prevProps && prevProps.template
        ? getMapImageWithTheme(template, theme)
        : "";
    const isMapChanged = prevMapImage !== currentMapImage;

    if (instance && panzoomedRef?.current && (isSpaceChanged || isMapChanged)) {
      reposition(instance);
    }

    if (resetCenter) {
      resetMapToCenter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, selectedCurrentSpaceId]);

  if (!isTypeOfIMapTemplate(template)) {
    return null;
  }

  return (
    <div
      style={{
        width: template.width ?? SVG_WIDTH,
        height: template.height ?? SVG_HEIGHT,
      }}
      ref={panzoomedRef}
      className={classNames(
        classes.root,
        "re-pan-container",
        (template.code ?? "").toLowerCase(),
      )}
    >
      {!isLoading && (
        <>
          <LayoutComponent isLockTablesEnabled />
          <BackgroundSVG spaceConfig={template} onLoad={initializePanzoom} />
          {isSponsorsLoaded && event?.id && template?.sponsors && (
            <EventContentWrapper
              eventId={event.id}
              sponsors={sponsors}
              templateSlots={template.sponsors}
              currentFloorId={selectedCurrentSpaceId}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SVGMapView;
